import React from 'react';
import { Col, Row } from 'reactstrap';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import CommonButton from '../CommonButton';
import { FormInputField, FormInputFieldType } from '../formFields';
import { sendQuestions } from '../../../customsite/CustomSiteActions';
import phoneIcon from '../../../assets/img/icons/phone.svg';
import mailIcon from '../../../assets/img/icons/mail.svg';
import FormSelectField from '../formFields/FormSelectField';
import { constantToSelect } from '../../helpers/constantToSelect';
import { DealerPositionName } from '../../../constants/DealerPosition';
import { EnquiryPurposeName } from '../../../constants/EnquiryPurpose';
import { ContactUsFormSchema } from './ContactUsForm.schema';
import Alert, { AlertVariantType } from '../new/Alert';

const dealerRole = constantToSelect(DealerPositionName);
const enquiryPurpose = constantToSelect(EnquiryPurposeName);

function ContactUsForm({ light }) {
  const dispatch = useDispatch();
  const profileEmail = useSelector((state) => state.myProfile.get('myProfileDetails').email);
  const initialValues = {
    name: '',
    email: profileEmail?.toLowerCase() || '',
    dealershipName: '',
    position: '',
    contactNumber: '',
    enquiryPurpose: '',
    message: '',
    terms: false,
  };

  const handleContactForm = (values, { setSubmitting, resetForm }) => {
    dispatch(sendQuestions(values))
      .then(() => {
        resetForm();
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Row className={`contact-us ${light ? 'light' : ''}`}>
      <Col sm={12} xxl={4} className="contact-us-left">
        <Row className="contact-us-card m-0 mt-4 mb-4 text-uppercase">
          <div className="contact-us-card__icon-box d-flex justify-content-center align-items-center">
            <img src={phoneIcon} alt="contact-us-phone" />
          </div>
          <Col className="ms-1 my-auto">
            <p className="text-xl mb-1">Call us</p>
            <p className="text-base">0303 0034714</p>
          </Col>
        </Row>
        <Row className="contact-us-card m-0 mb-4 text-uppercase">
          <div className="contact-us-card__icon-box d-flex justify-content-center align-items-center">
            <img src={mailIcon} alt="contact-us-mail" />
          </div>
          <Col className="ms-1 my-auto">
            <p className="text-xl mb-1 text-uppercase">Email us</p>
            <p className="text-base text-lowercase">info@thestockvault.com</p>
          </Col>
        </Row>
      </Col>
      <Col
        sm={12}
        xxl={8}
        className="contact-us-right d-flex justify-content-center align-items-center p-4"
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleContactForm}
          validationSchema={ContactUsFormSchema}
          enableReinitialize
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form className="contact-us-form w-100">
              <Row>
                <Col />
              </Row>
              <Row>
                <Col sm={12} xl={6} className="mt-3">
                  <FormInputField
                    name="name"
                    label={<span>Name</span>}
                    labelVariant="bordered"
                    nolabel={false}
                    required
                  />
                </Col>
                <Col sm={12} xl={6} className="mt-3">
                  <FormInputField
                    name="email"
                    label="Email"
                    labelVariant="bordered"
                    required
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} xl={6} xxl={4} className="mt-3">
                  <FormInputField
                    name="dealershipName"
                    label="Dealership name"
                    labelVariant="bordered"
                  />
                </Col>
                <Col sm={12} xl={6} xxl={4} className="mt-3">
                  <FormSelectField
                    name="position"
                    label="Position"
                    options={dealerRole}
                    labelVariant="bordered"
                    loadRightAway={false}
                  />
                </Col>
                <Col sm={12} xl={6} xxl={4} className="mt-3">
                  <FormInputField
                    name="contactNumber"
                    label="Contact number"
                    labelVariant="bordered"
                  />
                </Col>
                <Col sm={12} xl={6} xxl={12} className="mt-3">
                  <FormSelectField
                    name="enquiryPurpose"
                    label="Purpose of enquiry"
                    options={enquiryPurpose}
                    labelVariant="bordered"
                    required
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="mt-3">
                  <FormInputField
                    name="message"
                    type={FormInputFieldType.TEXTAREA}
                    label="Enquiry"
                    labelVariant="bordered"
                    required
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="mt-3">
                  <label className="contact-us__checkbox">
                    <input
                      className="contact-us__checkbox-hidden"
                      type="checkbox"
                      name="terms"
                      checked={values.terms}
                      onChange={(e) => {
                        setFieldValue('terms', e.target.checked, false);
                      }}
                    />
                    <span className="contact-us__checkbox-custom-icon" />
                    <span className="contact-us__checkbox-label">
                      By submitting this form, I agree to the StockVault Privacy Policy.
                    </span>
                  </label>
                  <div className="error-label">
                    {touched.terms && errors.terms ? errors.terms : ''}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col sm={12}>
                  <CommonButton type="submit" label="Submit" className="text-sm w-100" />
                </Col>
              </Row>
              <Row className={Object.keys(errors)?.length ? 'visible' : 'hidden'}>
                <Col sm={12} className="mt-4 mt-xl-5">
                  <Alert
                    variant={AlertVariantType.ERROR}
                    text="Please fill out all required fields."
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  );
}

ContactUsForm.defaultProps = {
  light: false,
};

ContactUsForm.propTypes = {
  light: PropTypes.bool,
};

export default ContactUsForm;
